import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuditTable = ({ entries, onSelectEntry, selectedEntry, currentPage, totalPages, onPageChange }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });

  const getSensitivityBadge = (level) => {
    const badges = {
      public: { color: 'bg-success/10 text-success', label: 'Öffentlich' },
      internal: { color: 'bg-primary/10 text-primary', label: 'Intern' },
      confidential: { color: 'bg-warning/10 text-warning', label: 'Vertraulich' },
      restricted: { color: 'bg-error/10 text-error', label: 'Streng vertraulich' }
    };
    const badge = badges?.[level] || badges?.internal;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge?.color}`}>
        {badge?.label}
      </span>
    );
  };

  const getActionIcon = (action) => {
    const icons = {
      create: 'Plus',
      update: 'Edit',
      delete: 'Trash2',
      view: 'Eye',
      export: 'Download',
      login: 'LogIn',
      logout: 'LogOut'
    };
    return icons?.[action] || 'Activity';
  };

  const getActionColor = (action) => {
    const colors = {
      create: 'text-success',
      update: 'text-primary',
      delete: 'text-error',
      view: 'text-muted-foreground',
      export: 'text-warning',
      login: 'text-success',
      logout: 'text-muted-foreground'
    };
    return colors?.[action] || 'text-foreground';
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto scrollbar-thin">
        <table className="w-full">
          <thead className="bg-muted sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/80" onClick={() => handleSort('timestamp')}>
                <div className="flex items-center gap-2">
                  Zeitstempel
                  <Icon name={sortConfig?.key === 'timestamp' ? (sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown') : 'ArrowUpDown'} size={14} />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/80" onClick={() => handleSort('user')}>
                <div className="flex items-center gap-2">
                  Benutzer
                  <Icon name={sortConfig?.key === 'user' ? (sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown') : 'ArrowUpDown'} size={14} />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Aktion
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Betroffene Daten
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Sensibilität
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                IP-Adresse
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {entries?.map((entry) => (
              <tr
                key={entry?.id}
                onClick={() => onSelectEntry(entry)}
                className={`cursor-pointer hover:bg-muted/50 transition-colors ${selectedEntry?.id === entry?.id ? 'bg-primary/5' : ''}`}
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">
                      {new Date(entry.timestamp)?.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(entry.timestamp)?.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">{entry?.userName}</span>
                    <span className="text-xs text-muted-foreground">{entry?.userId}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Icon name={getActionIcon(entry?.action)} size={16} className={getActionColor(entry?.action)} />
                    <span className="text-sm text-foreground">{entry?.actionLabel}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">{entry?.affectedRecord}</span>
                    <span className="text-xs text-muted-foreground">{entry?.recordType}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {getSensitivityBadge(entry?.sensitivityLevel)}
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-muted-foreground font-mono">{entry?.ipAddress}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      <div className="border-t border-border bg-card px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="FileText" size={16} />
          Seite {currentPage} von {totalPages} ({entries?.length} Einträge)
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            iconName="ChevronLeft"
            iconSize={16}
          >
            Zurück
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            iconName="ChevronRight"
            iconPosition="right"
            iconSize={16}
          >
            Weiter
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuditTable;